import { Link } from "@mui/material";
import { faqs, howItWorks } from "./helpers";
import { routes } from "@/global/routes";
import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";
import BackgroundImage from "public/images/athletics/1486x148_PickYourPack.jpg";
import BannerImage from "public/images/athletics/1486x148_PickYourPack.jpg";
import LogoImage from "public/images/athletics/oakland_athletics_logo.png";
import { TeamConfig } from "../types";
import { vendorUUIDs } from "../../constants";
import { AthleticsHamburgerListItems } from "../../tc_helper_athletics";
import {
  DefaultRegistrationTerms,
  DefaultCheckoutTerms,
} from "../../default_terms_and_conditions";

const config: TeamConfig = {
  teamSchema: "athletics_flex",
  browserTitle: "Oakland Athletics",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Oakland Athletics",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10180-athletics-sec",
    mapId: "main",
  },

  buckets: {
    enabled: true,
    list: [
      {
        id: 1,
        name: "Premium Games",
        description:
          "Add 1 or more Premium Games and unlock access to Regular Season Games for just $5.",
        max: 999,
        min: 1,
        remainingBucketLanguage: "Select a Premium Game to get started!",
        isPrimary: true,
      },
      {
        id: 2,
        name: "Regular Season",
        description: "The more Premium Games you select, the more you unlock!",
        min: 1,
        max: 2,
        shouldUseMaxAmountOfBucketId: 1,
        remainingBucketLanguage: "Add a Regular Season game for just $5!",
      },
    ],
  },

  useFirstAndLastNames: false,

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: "SECTION-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    isPerksEnabled: false,

    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://www.mlb.com/athletics",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    findGamesBannerImage: BannerImage.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage.src,
    logoImage: LogoImage.src + "?width=85&height=44",
    logoNameImage: "",
    faviconImage: LogoImage.src,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
      overlayLogoWidth: 150,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    shouldDisplayServiceFee: false,
    hideServiceFee: true,

    hidePackageBalanceOnCheckout: true,

    authPage: {
      hideWrongAccountOption: true,
    },

    viewTicketsUrl:
      "https://mlb.tickets.com/?agency=ATHM_MYTIXX&orgid=36207&_ga=2.254934476.519141554.1592235867-1927316541.1590535669&_gl=1*pgeeha*_gcl_au*NjI4NjkwOTA4LjE3MjA4MTI4OTQ.*_ga*NzYzMTM2NzAuMTcxOTQzMTc1Nw..*_ga_N8YFCZLYSZ*MTcyMjM5Mjc4Mi40LjAuMTcyMjM5Mjc4Mi42MC4wLjU0NDIxMTkwNA..#/auth/login",
  },

  colors: {
    primary: "#003831",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <AthleticsHamburgerListItems />,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarSuccessText:
        "You're good to go! Add more Premium Games or checkout now!",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Order Confirmed!",
    checkoutSuccessDescription: (
      <>
        Thank you for your purchase with the Oakland Athletics. Log in and
        access your Tickets via the{" "}
        <Link
          target="_blank"
          href="https://mandrillapp.com/track/click/30264162/www.mlb.com?p=eyJzIjoiUnFCVS1jWksxdFZMdzJpT2hBdGtVTVpfSUxRIiwidiI6MSwicCI6IntcInVcIjozMDI2NDE2MixcInZcIjoxLFwidXJsXCI6XCJodHRwczpcXFwvXFxcL3d3dy5tbGIuY29tXFxcL2F0aGxldGljc1xcXC9iYWxscGFya1wiLFwiaWRcIjpcIjA3NjQyOGVkMmYzMDQ2NmY5ZDJjMTdiNTA0Y2FjMjUxXCIsXCJ1cmxfaWRzXCI6W1wiNjgwMWQ5NTYzYTI1ZmIzYzg5YTdmZGQ0YTJiODFlZmMzZWVmNDgwNVwiXX0ifQ"
        >
          MLB Ballpark app
        </Link>{" "}
        using the same email you used to purchase your tickets. For questions
        regarding this order please contact A&apos;s Ticket Services at{" "}
        <Link href="tel:510-568-5600">510-568-5600</Link>.
      </>
    ),

    giftCard: {
      title: "Give The Gift Of Broadway",
      createGiftCard: {
        description:
          "Share the magic of Broadway with a friend or loved one. Upon purchase of a gift card, a unique code will be generated for the intended recipient to redeem. Once redeemed, the gift card is not refundable.",
        recipient: {
          sendNowFalse: {
            description:
              "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift card, they will be prompted to enter their unique code to redeem the gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Hippodrome Broadway Series shows in Baltimore.",
          },
          sendNowTrue: {
            description:
              "The Recipient will be sent an email with the unique code for them to redeem their gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Hippodrome Broadway Series shows in Baltimore.",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore account above.",
        },
        logout: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore.",
        },
        success: {
          description:
            "has been added to your Hippodrome Broadway Series in Baltimore Account Manager account!",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",

    registerPageLanguage:
      "Please provide your cell phone number for ticket delivery.",

    optionsMenuLoginButtonLanguage: "View Account",
  },
};

export default config;
